import i18nFactory from '@yandex-int/i18n';
import { isNumber } from 'lodash';
import { Moment } from 'moment';

import {
    BannerCriteriaKey,
    CampaignCriteriaKey,
    DealsCriteriaKey,
    FeedFilterCriteriaKey,
    FilterBooleanValueType,
    GroupCriteriaKey,
    PhraseCriteriaKey,
    PricePackagesCriteriaKey,
    RetargetingCriteriaKey,
    UserProfileCriteriaKey
} from '../../declarations/filter/filter';
import { parseNumber } from '../../service/FormatHelpers/FormatHelpers';
import { LangUtils } from '../../service/Lang/Lang';
import { CampaignsCalcTypeFilter, CampaignType } from '../../typings/domain';
import {
    CriteriaType,
    EnumSetItem,
    FilterExpression,
    FilterExpressionEnumSetValue,
    FilterExpressionValue,
    FilterExpressionValueData,
    FilterExpressionValueType,
    FilterPartialExpression,
    Operator
} from '../../typings/filter';

import * as keyset from './filter.i18n';

const i18n = i18nFactory(keyset);

const criteriaOperators: { [key in CriteriaType]: Operator[] } = {
    [CriteriaType.text]: [Operator.CONTAINS, Operator.NOT_CONTAINS],
    [CriteriaType.positiveNumber]: [Operator.GTE, Operator.LTE],
    [CriteriaType.enumSet]: [Operator.EQUALS],
    [CriteriaType.enumSetRadio]: [Operator.EQUALS],
    [CriteriaType.date]: [Operator.GTE, Operator.LTE],
    [CriteriaType.positiveNumberSet]: [Operator.EQUALS],
    [CriteriaType.boolean]: [Operator.EQUALS]
};

export const getOperatorsForCriteriaType = (type: CriteriaType): Operator[] => {
    if (!(type in criteriaOperators)) {
        throw new Error(`Unknown criteria type  ${type}`);
    }

    return criteriaOperators[type];
};

// значения захардкожены, а не взяты из танкера, так как они используются в файле filter.ne и их необходимо менять в обоих местах в коде
const operatorContainsOrNotContains = {
    contains: {
        ru: 'содержит', // tslint:disable-line:no-rus-string-locales
        uk: 'содержит', // tslint:disable-line:no-rus-string-locales
        en: 'contains',
        tr: 'içeriyor'
    },
    'not-contains': {
        ru: 'не содержит', // tslint:disable-line:no-rus-string-locales
        uk: 'не содержит', // tslint:disable-line:no-rus-string-locales
        en: 'doesn’t contain',
        tr: 'içermiyor'
    }
};

const lang = LangUtils.getCurrentLang();

const operatorToStringDict: { [key in Operator]: string } = {
    [Operator.LTE]: '<=',
    [Operator.GTE]: '>=',
    [Operator.CONTAINS]: operatorContainsOrNotContains.contains[lang],
    [Operator.NOT_CONTAINS]: operatorContainsOrNotContains['not-contains'][lang],
    [Operator.EQUALS]: '=',
    [Operator.NOT_EQUALS]: '!=',
    [Operator.COLON]: ':'
};

const stringToOperatorDict = Object.keys(operatorToStringDict).reduce((acc, key) => {
    acc[operatorToStringDict[key]] = key;

    return acc;
}, {});

export const stringToOperator = (str: string): Operator => {
    if (!(str in stringToOperatorDict)) {
        throw new Error(`Unknown operator ${str}`);
    }

    return stringToOperatorDict[str];
};

export const operatorToString = (operator: Operator): string => {
    if (!(operator in operatorToStringDict)) {
        throw new Error(`Unknown operator ${operator}`);
    }

    return operatorToStringDict[operator];
};

export const valueToString = (value: FilterExpressionValue): string => {
    switch (value.type) {
        case FilterExpressionValueType.partial:
        case FilterExpressionValueType.number:
        case FilterExpressionValueType.text:
        case FilterExpressionValueType.partialDate:
            return <string>value.value;
        case FilterExpressionValueType.numberSet:
            return (<number[]>value.value).join(', ');
        case FilterExpressionValueType.enumSet:
            return (<FilterExpressionEnumSetValue>value.value)
                .filter(item => item.title !== '')
                .map(item => item.title)
                .join(', ');
        case FilterExpressionValueType.enumSetRadio:
            return (<EnumSetItem>value.value).title;
        case FilterExpressionValueType.date:
            const date = <Moment>value.value;

            return date.isValid() ? date.format('DD.MM.YYYY') : i18n('incorrect-date');
        case FilterExpressionValueType.boolean:
            return value.value === true ? FilterBooleanValueType.ENABLED : FilterBooleanValueType.DISABLED;
        default:
            throw new Error(`Unsupported value type: ${value.type}`);
    }
};

export const valueToStringForSuggest = (value: FilterExpressionValue): string => {
    switch (value.type) {
        case FilterExpressionValueType.partialDate:
            const parts = (<string>value.value).split('.');
            const placeholders = [i18n('day-placeholder'), i18n('month-placeholder'), i18n('year-placeholder')];

            return placeholders.map((item, pos) => parts[pos] || item).join('.');
        default:
            return valueToString(value);
    }
};

export const expressionToString = (expression: FilterPartialExpression, isUrlStringify?: boolean): string => {
    let field = expression.field;
    let separatorField = '';
    let additionalFieldTitle = '';
    let operator = '';
    let value = '';

    if (expression.operator) {
        if (expression.operator in Operator) {
            operator = ' ' + operatorToString(expression.operator as Operator);
        } else {
            operator = ' ' + expression.operator;
        }
    }

    if (expression.value !== undefined) {
        value = ' ' + valueToString(expression.value);
    }

    if (expression.separatorField !== undefined) {
        separatorField = expression.separatorField;
    }

    if (expression.additionalField !== undefined) {
        additionalFieldTitle = isUrlStringify
            ? expression.additionalField.key || ''
            : expression.additionalField.title || '';
    }

    /*
     * При вводе части оператора и выбора из подсказки
     * Надо затереть введенную уже часть, а иначе она распарсится как часть имени поля
     */
    const fieldParts = field.split(' ');
    const lastFieldPart = fieldParts.pop() || '';
    if (operator.trim().includes(lastFieldPart)) {
        field = fieldParts.join(' ');
    }

    const additionalFieldParts = additionalFieldTitle.split(' ');
    const lastAdditionalFieldPart = additionalFieldParts.pop() || '';
    if (operator.trim().includes(lastAdditionalFieldPart)) {
        additionalFieldTitle = additionalFieldParts.join(' ');
    }

    return `${field}${separatorField}${additionalFieldTitle}${operator}${value}`;
};

type FilterValueMapper<T> = (value: FilterExpressionValue) => T;

export class FilterCriteriaMappings<T> {
    valueMappings = {
        toBoolean: (v: FilterExpressionValue): boolean => v.value as boolean,
        toNumber: (v: FilterExpressionValue): number => v.value as number,
        toDate: (v: FilterExpressionValue): string => (v.value as Moment).format('YYYY-MM-DD'),
        toEnum: <EnumCls extends {}>(
            enumObj: EnumCls
        ): ((v: FilterExpressionValue) => Array<EnumCls[keyof EnumCls]>) => (
            v: FilterExpressionValue
        ): Array<EnumCls[keyof EnumCls]> => {
            const res = (v.value as FilterExpressionEnumSetValue).map(item => {
                const { key } = item;

                if (!(key in enumObj)) {
                    throw `Invalid enum key ${key} for ${JSON.stringify(enumObj)}`;
                }
                return enumObj[key];
            });

            return res;
        },
        toEnumRadio: <EnumCls extends {}>(enumObj: EnumCls): ((v: FilterExpressionValue) => Array<EnumCls>) => (
            v: FilterExpressionValue
        ): Array<EnumCls> => {
            const { key } = v.value as EnumSetItem;

            if (!(key in enumObj)) {
                throw `Invalid enum key ${key} for ${JSON.stringify(enumObj)}`;
            }

            return enumObj[key];
        }
    };

    equality = <K extends keyof T>(
        eqField: K,
        valueMapper: FilterValueMapper<T[K]>
    ): ((exp: FilterExpression) => { [x: string]: T[K] }) => ({
        operator,
        value
    }: FilterExpression): { [x: string]: T[K] } => ({
        [eqField]: valueMapper(value)
    });

    boolean = <K extends keyof T>(
        field: K,
        valueMapper: FilterValueMapper<T[K]>
    ): ((exp: FilterExpression) => { [x: string]: boolean }) => ({
        operator,
        value
    }: FilterExpression): { [x: string]: boolean } => ({
        [field]: operator === Operator.EQUALS ? !!valueMapper(value) : !valueMapper(value)
    });

    comparison = <K1 extends keyof T, K2 extends keyof T, K extends keyof T>(
        // TODO DIRECT-73317: или абстрагировать stats, или убрать класс маппингов
        minField: K1,
        maxField: K2,
        valueMapper: FilterValueMapper<T[K]>,
        isStatField: boolean = true,
        additionalCriteriaKey?: string
    ): ((
        exp: FilterExpression
    ) =>
        | { stats: { [x: string]: T[K] } }
        | { goalStats: Array<{ [x: string]: T[K] | string }> }
        | { [x: string]: T[K] }) => ({
        operator,
        value
    }: FilterExpression):
        | { stats: { [x: string]: T[K] } }
        | { [x: string]: T[K] }
        | { goalStats: Array<{ [x: string]: T[K] | string }> } => {
        const filter =
            operator === Operator.GTE ? { [minField]: valueMapper(value) } : { [maxField]: valueMapper(value) };

        if (isStatField) {
            return {
                stats: filter
            };
        } else if (additionalCriteriaKey) {
            return {
                goalStats: [
                    {
                        ...filter,
                        goalId: additionalCriteriaKey
                    }
                ]
            };
        } else {
            return filter;
        }
    };

    includes = <K1 extends keyof T, K2 extends keyof T>(
        includesField: K1,
        notIncludesField: K2
    ): ((exp: FilterExpression) => { [x: string]: number[] }) => ({
        operator,
        value
    }: FilterExpression): { [x: string]: number[] } => {
        const ids = value.value as number[];
        return operator === Operator.EQUALS ? { [includesField]: ids } : { [notIncludesField]: ids };
    };

    contains = <K1 extends keyof T, K2 extends keyof T>(
        containsField: K1,
        notContainsField: K2
    ): ((exp: FilterExpression) => { [x: string]: string }) => ({
        operator,
        value
    }: FilterExpression): { [x: string]: string } => {
        return operator === Operator.CONTAINS
            ? { [containsField]: value.value as string }
            : { [notContainsField]: value.value as string };
    };
}

const numberValueToApiFormat = (value: string | number): number => {
    if (isNumber(value)) {
        return value;
    }
    return parseNumber(value);
};

const valueToApiFormat = (
    type: FilterExpressionValueType,
    value: FilterExpressionValueData
): FilterExpressionValueData => {
    const typeToFormatMap = {
        number: numberValueToApiFormat
    };

    return typeToFormatMap[type] ? typeToFormatMap[type](value) : value;
};

export const expressionToApiFormat = (expression: FilterExpression): FilterExpression => {
    const { type, value } = expression.value;

    return {
        ...expression,
        value: {
            type: type,
            value: valueToApiFormat(type, value)
        }
    };
};

export const textToCampaignFilterExpression = (value: string): FilterExpression => {
    return {
        criteriaKey: CampaignCriteriaKey.name,
        operator: Operator.CONTAINS,
        value: {
            type: FilterExpressionValueType.text,
            value: value
        }
    };
};

export const textToGroupFilterExpression = (value: string): FilterExpression => {
    return {
        criteriaKey: GroupCriteriaKey.name,
        operator: Operator.CONTAINS,
        value: {
            type: FilterExpressionValueType.text,
            value: value
        }
    };
};

export const textToPhraseFilterExpression = (value: string): FilterExpression => {
    return {
        criteriaKey: PhraseCriteriaKey.keyword,
        operator: Operator.CONTAINS,
        value: {
            type: FilterExpressionValueType.text,
            value: value
        }
    };
};

export const textToFeedFilterFilterExpression = (value: string): FilterExpression => {
    return {
        criteriaKey: FeedFilterCriteriaKey.name,
        operator: Operator.CONTAINS,
        value: {
            type: FilterExpressionValueType.text,
            value: value
        }
    };
};

export const textToRetargetingFilterExpression = (value: string): FilterExpression => {
    return {
        criteriaKey: RetargetingCriteriaKey.name,
        operator: Operator.CONTAINS,
        value: {
            type: FilterExpressionValueType.text,
            value: value
        }
    };
};

export const textToUserProfileFilterExpression = (value: string): FilterExpression => {
    return {
        criteriaKey: UserProfileCriteriaKey.name,
        operator: Operator.CONTAINS,
        value: {
            type: FilterExpressionValueType.text,
            value: value
        }
    };
};

export const textToPricePackagesFilterExpression = (value: string): FilterExpression => {
    return {
        criteriaKey: PricePackagesCriteriaKey.name,
        operator: Operator.CONTAINS,
        value: {
            type: FilterExpressionValueType.text,
            value: value
        }
    };
};

export const textToBannerFilterExpression = (value: string): FilterExpression => {
    return {
        criteriaKey: BannerCriteriaKey.title,
        operator: Operator.CONTAINS,
        value: {
            type: FilterExpressionValueType.text,
            value: value
        }
    };
};

export const textToDealFilterExpression = (value: string): FilterExpression => {
    return {
        criteriaKey: DealsCriteriaKey.name,
        operator: Operator.CONTAINS,
        value: {
            type: FilterExpressionValueType.text,
            value: value
        }
    };
};

const CpmCampaignsHash = {
    [CampaignType.CPM_BANNER]: true,
    [CampaignType.CPM_PRICE]: true
};

/**
 * Возвращает CalcFilter по типу кампании
 * @param campaignType
 */
export const mapCampaignTypeToCalcType = (campaignType?: CampaignType): CampaignsCalcTypeFilter => {
    return campaignType && CpmCampaignsHash[campaignType] ? CampaignsCalcTypeFilter.CPM : CampaignsCalcTypeFilter.CPC;
};
